import React from 'react';
import TestResultDisplay from '../../common/TestResultDisplay';

// 测试项目接口
interface TestItem {
  name: string;
  passed: boolean;
  userAnswer?: string;
  correctAnswer?: string;
  details?: string;
}

// 测试组接口
interface TestGroup {
  title: string;
  subtitle?: string;
  score?: { current: number; total: number };
  items: TestItem[];
  passed: boolean;
  note?: string;
  hasLogicalError?: boolean;
  errorReason?: string;
}

interface CognitionSummaryDisplayProps {
  responses: any;
  cognitionStatus?: 'PASS' | 'FAIL' | 'INCONCLUSIVE';
  className?: string;
}

const CognitionSummaryDisplay: React.FC<CognitionSummaryDisplayProps> = ({ 
  responses, 
  cognitionStatus,
  className = '' 
}) => {

  // 判断认知是否通过 - 基于传入的状态
  const cognitionPassed = cognitionStatus === 'PASS';
  
  // 从真实的responses数据中获取认知评估数据
  const getRealCognitionData = () => {
    
    // 方式1: 从filterQuestionResult字段获取 (修复关键逻辑)
    if (responses?.filterQuestionResult !== undefined) {
      return {
        filterQuestion: responses.filterQuestionResult,
        testResults: responses.testResults || [],
        cognitiveTests: responses.cognitiveTests,
        wordRecall: responses.wordRecall
      };
    }
    
    // 方式2: 从直接字段获取
    if (responses?.filterQuestion !== undefined) {
      return {
        filterQuestion: responses.filterQuestion,
        cognitiveTests: responses.cognitiveTests,
        wordRecall: responses.wordRecall
      };
    }
    
    // 方式3: 从testResults中获取rawUserAnswers
    if (responses?.testResults) {
      const cognitionTests = responses.testResults.filter((test: any) => 
        test.name === "认知问题筛查" || test.name === "时间定向" || 
        test.name === "空间定向" || test.name === "三词记忆"
      );
      
      if (cognitionTests.length > 0) {
        // 合并所有认知测试的rawUserAnswers数据
        const combinedData: any = {
          testResults: cognitionTests
        };
        
        // ⚠️ 注意：这里不再推断filterQuestion，因为可能与实际的filterQuestionResult冲突
        // 让上级逻辑处理筛查问题的判断
        
        return combinedData;
      }
    }
    
    // 方式4: 从domainResult中的testResults获取
    if ((responses as any)?.status && (responses as any)?.testResults) {
      const cognitionTests = (responses as any).testResults.filter((test: any) => 
        test.name === "认知问题筛查" || test.name === "时间定向" || 
        test.name === "空间定向" || test.name === "三词记忆"
      );
      
      if (cognitionTests.length > 0) {
        const combinedData: any = {
          testResults: cognitionTests
        };
        
        // ⚠️ 注意：这里也不再推断filterQuestion，避免逻辑冲突
        // 如果有filterQuestionResult字段，应该由方式1处理
        
        if (Object.keys(combinedData).length > 0) {
          return combinedData;
        }
      }
    }
    
    return null;
  };
  
  // 检查是否只有筛查数据且筛查失败
  const realData = getRealCognitionData();
  
  // 🔧 统一逻辑：所有评估保持一致
  // 统一标准：filterQuestion: true = 筛查通过(无困难), false = 筛查失败(有困难)
  const filterTest = realData?.testResults?.find((test: any) => test.name === "认知问题筛查");
  const isFilterQuestionFailed = realData?.filterQuestion === false;  // 统一逻辑：false才是筛查失败
  
  // 完整的认知测试 - 基于真实数据生成
  const generateCognitionTestData = (): TestGroup[] => {
    
    if (!realData || !realData.testResults) {
      return [
        {
          title: '认知状况评估',
          passed: cognitionPassed,
          items: [
            {
              name: '评估状态',
              passed: cognitionPassed,
              userAnswer: cognitionPassed ? '认知功能正常' : '认知功能需要关注'
            }
          ]
        }
      ];
    }

    const testGroups: TestGroup[] = [];
    
    
    // 获取当前时间信息用于显示正确答案
    const getCurrentTimeInfo = () => {
      const now = new Date();
      const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
      return {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        date: now.getDate(),
        weekdayName: weekdays[now.getDay()]
      };
    };
    const timeInfo = getCurrentTimeInfo();

    // 🎯 移除重复的筛查问题显示 - 上方已经有统一的筛查问题显示
    // 这里只显示具体的认知测试结果（时间定向、空间定向、三词记忆等）

    // 时间定向测试 - 基于真实数据
    const timeTest = realData.testResults.find((test: any) => test.name === "时间定向");
    if (timeTest?.rawUserAnswers) {
      const answers = timeTest.rawUserAnswers.answers || {};
      const results = timeTest.rawUserAnswers.results || {};
      const correctCount = Object.values(results).filter((r: any) => r === true).length;
      
      const items: TestItem[] = [];
      if (answers.year !== undefined) {
        items.push({
          name: '年份',
          passed: results.year,
          userAnswer: answers.year || '未作答',
          correctAnswer: `${timeInfo.year}年`
        });
      }
      if (answers.month !== undefined) {
        items.push({
          name: '月份',
          passed: results.month,
          userAnswer: answers.month || '未作答',
          correctAnswer: `${timeInfo.month}月`
        });
      }
      if (answers.date !== undefined) {
        items.push({
          name: '日期',
          passed: results.date,
          userAnswer: answers.date || '未作答',
          correctAnswer: `${timeInfo.date}日`
        });
      }
      if (answers.weekday !== undefined) {
        items.push({
          name: '星期',
          passed: results.weekday,
          userAnswer: answers.weekday || '未作答',
          correctAnswer: `星期${timeInfo.weekdayName}`
        });
      }
      
      if (items.length > 0) {
        testGroups.push({
          title: '时间定向测试',
          score: { current: correctCount, total: items.length },
          passed: timeTest.passed,
          items: items
        });
      }
    }

    // 空间定向测试 - 基于真实数据
    const spaceTest = realData.testResults.find((test: any) => test.name === "空间定向");
    if (spaceTest?.rawUserAnswers) {
      const answers = spaceTest.rawUserAnswers.answers || {};
      const validation = spaceTest.rawUserAnswers.validation || {};
      
      const items: TestItem[] = [];
      if (answers.city !== undefined) {
        items.push({
          name: '城市',
          passed: !!answers.city,
          userAnswer: answers.city || '未作答'
        });
      }
      if (answers.place !== undefined) {
        items.push({
          name: '地点',
          passed: !!answers.place,
          userAnswer: answers.place || '未作答'
        });
      }
      if (answers.area !== undefined) {
        items.push({
          name: '区域',
          passed: !!answers.area,
          userAnswer: answers.area || '未作答'
        });
      }
      if (answers.transport !== undefined) {
        items.push({
          name: '交通方式',
          passed: !!answers.transport,
          userAnswer: answers.transport || '未作答'
        });
      }
      
      if (items.length > 0) {
        testGroups.push({
          title: '空间定向测试',
          passed: spaceTest.passed,
          items: items,
          hasLogicalError: !validation.passed,
          errorReason: validation.details?.errorReason
        });
      }
    }

    // 三词记忆测试 - 基于真实数据
    const wordTest = realData.testResults.find((test: any) => test.name === "三词记忆");
    if (wordTest?.rawUserAnswers) {
      const answers = wordTest.rawUserAnswers.answers || {};
      const targetWords = ['花', '门', '米饭']; // 标准ICOPE词汇
      const selectedWords = answers.selectedWords || [];
      const correctCount = answers.correctCount || 0;
      
      testGroups.push({
        title: '三词记忆测试',
        score: { current: correctCount, total: 3 },
        passed: wordTest.passed,
        items: [
          {
            name: '词汇记忆',
            passed: wordTest.passed,
            userAnswer: selectedWords.length > 0 ? selectedWords.join('、') : '未选择',
            correctAnswer: targetWords.join('、')
          }
        ]
      });
    }

    // 如果没有任何真实测试数据，显示状态信息
    if (testGroups.length === 0) {
      testGroups.push({
        title: '认知状况评估',
        passed: cognitionPassed,
        items: [
          {
            name: '评估结果',
            passed: cognitionPassed,
            userAnswer: cognitionPassed ? '认知功能正常' : '认知功能需要关注'
          }
        ]
      });
    }

    return testGroups;
  };

  const testGroups = generateCognitionTestData();

  return (
    <TestResultDisplay
      title="认知能力评估结果"
      subtitle="完整认知功能测试"
      testGroups={testGroups}
      className={className}
    />
  );
};

export default CognitionSummaryDisplay;